import * as React from 'react'
import { cn } from '@/registry/reapit/lib/utils'

// ─── lookup maps (static strings so Tailwind v4 scanner picks them all up) ────

const directionClasses = {
  row: 'flex-row',
  col: 'flex-col',
  'row-reverse': 'flex-row-reverse',
  'col-reverse': 'flex-col-reverse',
} as const

const alignClasses = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
} as const

const justifyClasses = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
} as const

// gap-0.5 (2px) was removed — it is off the DS 4-px grid.
// Permitted values: 0, 1 (4px), 2 (8px), 3 (12px), 4 (16px), 5 (20px), 6 (24px),
//                   8 (32px), 10 (40px), 12 (48px), 16 (64px).
const gapClasses = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
  16: 'gap-16',
} as const

const gapXClasses = {
  0: 'gap-x-0',
  1: 'gap-x-1',
  2: 'gap-x-2',
  3: 'gap-x-3',
  4: 'gap-x-4',
  5: 'gap-x-5',
  6: 'gap-x-6',
  8: 'gap-x-8',
  10: 'gap-x-10',
  12: 'gap-x-12',
  16: 'gap-x-16',
} as const

const gapYClasses = {
  0: 'gap-y-0',
  1: 'gap-y-1',
  2: 'gap-y-2',
  3: 'gap-y-3',
  4: 'gap-y-4',
  5: 'gap-y-5',
  6: 'gap-y-6',
  8: 'gap-y-8',
  10: 'gap-y-10',
  12: 'gap-y-12',
  16: 'gap-y-16',
} as const

const flexBgClasses = {
  background: 'bg-background',
  muted: 'bg-muted',
  card: 'bg-card',
  accent: 'bg-accent',
  primary: 'bg-primary',
  none: '',
} as const

const flexRoundedClasses = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  full: 'rounded-full',
} as const

const flexPaddingXClasses = {
  none: '',
  sm: 'px-4',
  md: 'px-6',
  lg: 'px-8',
} as const

const flexPaddingYClasses = {
  none: '',
  sm: 'py-4',
  md: 'py-6',
  lg: 'py-8',
} as const

// ─── Flex ────────────────────────────────────────────────────────────────────

export namespace Flex {
  export type Direction = keyof typeof directionClasses
  export type Align = keyof typeof alignClasses
  export type Justify = keyof typeof justifyClasses
  export type Gap = keyof typeof gapClasses

  export interface Props extends React.ComponentProps<'div'> {
    /** Render as a different element (e.g. 'section', 'header', 'ul'). */
    as?: React.ElementType
    /** flex-direction. Default: 'row'. */
    direction?: Direction
    /** align-items. */
    align?: Align
    /** justify-content. */
    justify?: Justify
    /** gap between children (both axes). */
    gap?: Gap
    /** gap on the inline (horizontal) axis only. */
    gapX?: Gap
    /** gap on the block (vertical) axis only. */
    gapY?: Gap
    /** flex-wrap. */
    wrap?: boolean
    /** Renders as inline-flex instead of flex. */
    inline?: boolean
    /** Shorthand for align="center" justify="between". */
    between?: boolean
    /** Background colour. */
    background?: keyof typeof flexBgClasses
    /** Border radius. */
    rounded?: keyof typeof flexRoundedClasses
    /** Horizontal padding preset. */
    paddingX?: keyof typeof flexPaddingXClasses
    /** Vertical padding preset. */
    paddingY?: keyof typeof flexPaddingYClasses
  }
}

export function Flex({
  as: Comp = 'div',
  direction = 'row',
  align,
  justify,
  gap,
  gapX,
  gapY,
  wrap = false,
  inline = false,
  between = false,
  background,
  rounded,
  paddingX,
  paddingY,
  className,
  children,
  ...props
}: Flex.Props) {
  return (
    <Comp
      className={cn(
        inline ? 'inline-flex' : 'flex',
        directionClasses[direction],
        between ? 'items-center justify-between' : [align && alignClasses[align], justify && justifyClasses[justify]],
        gap !== undefined && gapClasses[gap],
        gapX !== undefined && gapXClasses[gapX],
        gapY !== undefined && gapYClasses[gapY],
        wrap && 'flex-wrap',
        background && flexBgClasses[background],
        rounded && flexRoundedClasses[rounded],
        paddingX && flexPaddingXClasses[paddingX],
        paddingY && flexPaddingYClasses[paddingY],
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── Stack ───────────────────────────────────────────────────────────────────

export namespace Stack {
  export interface Props extends Omit<Flex.Props, 'direction' | 'inline'> {}
}

/** Vertical flex container — shorthand for <Flex direction="col" />. */
export function Stack({ className, ...props }: Stack.Props) {
  return <Flex direction="col" className={className} {...props} />
}
